/**
 * Which way the connection actually went: straight there, or through the relay.
 *
 * This exists because the honest answer to "how often does direct work" is
 * not a number from someone else's network. Every connection reports what it
 * did, so the number can be Frank's own rather than a rule of thumb from
 * traffic that looks nothing like Uganda on mobile data.
 *
 * WebRTC knows the answer and will say so: the pair of addresses it settled
 * on is in its own statistics, and a relayed connection has "relay" on at
 * least one side.
 */

export const DIRECT = "direct";
export const RELAY = "relay";
export const UNKNOWN = "unknown";

/**
 * Reads the verdict out of a statistics report.
 *
 * Kept apart from the connection itself so it can be checked against reports
 * that are awkward to produce on purpose: a browser that reports no pair, a
 * pair that failed next to one that worked, a relay on the far side only.
 */
export function readPath(report) {
  if (!report) return { path: UNKNOWN, local: null, remote: null };
  const entries = [];
  report.forEach((value) => entries.push(value));

  const pairs = entries.filter((e) => e?.type === "candidate-pair");
  // Firefox marks the chosen pair "selected"; the others only mark it
  // nominated and succeeded. Both spellings mean the same thing here.
  const chosen =
    pairs.find((p) => p.selected && p.state === "succeeded") ||
    pairs.find((p) => p.nominated && p.state === "succeeded") ||
    pairs.find((p) => p.state === "succeeded");
  if (!chosen) return { path: UNKNOWN, local: null, remote: null };

  const local = report.get?.(chosen.localCandidateId)?.candidateType ?? null;
  const remote = report.get?.(chosen.remoteCandidateId)?.candidateType ?? null;
  if (!local && !remote) return { path: UNKNOWN, local, remote };

  // One relayed end is enough: the traffic goes through the relay either way.
  const path = local === "relay" || remote === "relay" ? RELAY : DIRECT;
  return { path, local, remote };
}

/** The same question, asked of a live connection. */
export async function pathOf(peerConnection) {
  try {
    return readPath(await peerConnection.getStats());
  } catch {
    return { path: UNKNOWN, local: null, remote: null };
  }
}

/** What to put on screen. Plain words: this is shown to users, not to me. */
export function describePath({ path }) {
  if (path === DIRECT) return "Straight to your other device, nothing in between.";
  if (path === RELAY) return "Through the Novamund relay, which cannot read it.";
  return "Connected. This browser does not say which route it took.";
}
